import React, { useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/autoplay";
import "swiper/css/pagination";

import { Autoplay, Pagination } from "swiper/modules";
import styled, { css, keyframes } from "styled-components";
import colors from "theme/colors";
import { Calendar, ChevronLeft, ChevronRight, Heart } from 'lucide-react';
import { Column } from "styled/alignment/Column";
import eventTypes from "theme/eventTypes";
import breakpoints from "theme/breakpoints";
import Text from "components/_UI/Text";
import { Row } from "styled/alignment/Row";
import { FullyCentered } from "styled/alignment/Center";
import Icon from "components/_UI/Icon";
import { useEventDetailModal } from "context/events/EventDetailModal";
import date from 'helpers/date';
import currency from 'helpers/currency';
import useFavorite from 'hooks/useFavorite';

const CarouselFavoriteButton = ({ slug, initialFavorited, eventName }) => {
    const { isFavorited, toggleFavorite } = useFavorite(slug, initialFavorited);
    return (
        <FavoriteContent
            as='button'
            type='button'
            aria-pressed={isFavorited}
            aria-label={isFavorited
                ? `Remover ${eventName || 'evento'} dos favoritos`
                : `Favoritar ${eventName || 'evento'}`}
            onClick={(e) => {
                e.stopPropagation();
                toggleFavorite();
            }}
        >
            <Heart
                size={14}
                color={isFavorited ? colors.occasionalPink : colors.night}
                fill={isFavorited ? colors.occasionalPink : 'none'}
            />
        </FavoriteContent>
    );
};

/**
 * Estado de preço do card: 'free', 'paid' ou null (sem selo).
 *
 * `min_price`/`is_free` vêm do endpoint de destaques e são a fonte correta:
 * refletem os lotes que existem hoje. `classification` só registra a intenção
 * declarada no cadastro, então serve apenas de fallback para as listas antigas,
 * que não trazem os campos de preço.
 *
 * `is_free: null` significa que o evento não vende ingresso (só produtos): sem
 * preço de entrada, o selo "Pago" seria uma informação inventada.
 */
function getPriceState(item) {
    if (item && Object.prototype.hasOwnProperty.call(item, 'is_free')) {
        if (item.is_free === null || item.is_free === undefined) return null;
        return item.is_free ? 'free' : 'paid';
    }

    return item?.classification?.name === eventTypes.free.slug ? 'free' : 'paid';
}

const EventCarousel = ({ data }) => {
  const { setEventDetailModal } = useEventDetailModal();
  const swiperRef = useRef(null);

  // WCAG 2.2.2 (Pause, Stop, Hide): movimento automático acima de 5s precisa ser
  // pausável. Quem pediu menos animação no sistema não recebe autoplay nenhum.
  const prefersReducedMotion = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  // Abaixo de 6 cards o Swiper não tem slides suficientes para girar, e a faixa
  // vira uma lista parada — a de destaques pode legitimamente chegar com quatro
  // eventos, o mínimo que o backend garante antes de ampliar a busca. Os três
  // comportamentos do modo giratório (loop, centralização e realce do card
  // central) pressupõem movimento, então caem juntos: parados, os cards alinham
  // à esquerda, preenchem a linha e têm todos o mesmo tamanho.
  const canLoop = data.length >= 6;

  const handleNext = () => swiperRef.current?.slideNext();
  const handlePrev = () => swiperRef.current?.slidePrev();

  const handleSlideClick = (item, index) => {
    // Trazer o card para o centro antes de abrir só se protege de clique errado
    // enquanto a faixa gira. Parada, os três cards estão inteiros na tela e com o
    // mesmo peso visual: exigir dois cliques nos laterais seria só um obstáculo.
    if (!canLoop || swiperRef.current?.realIndex === index) {
      setEventDetailModal(item);
      return;
    }
    swiperRef.current?.slideToLoop(index, 500);
  };

  return (
    <CarouselWrapper>
      <ArrowLeft onClick={handlePrev}>
        <ChevronLeft size={20} color={colors.night} />
      </ArrowLeft>
      <ArrowRight onClick={handleNext}>
        <ChevronRight size={20} color={colors.night} />
      </ArrowRight>

      <CarouselContainer>
        <StyledSwiper
          $emphasis={canLoop}
          slidesPerView={3}
          spaceBetween={12}
          centeredSlides={canLoop}
          loop={canLoop}
          breakpoints={{
              0: {
                  slidesPerView: 1,
                  spaceBetween: 0,
                  centeredSlides: false,
              },
              1200: {
                  slidesPerView: 3,
                  spaceBetween: 12,
                  centeredSlides: canLoop,
              },
          }}
          autoplay={prefersReducedMotion ? false : {
            delay: 6000,
            // Parar ao interagir e ao passar o mouse: sem isso o slide se move
            // debaixo do cursor no instante do clique e o usuário abre o evento errado.
            disableOnInteraction: true,
            pauseOnMouseEnter: true,
          }}
          pagination={{
            clickable: true,
          }}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          modules={[Autoplay, Pagination]}
        >
          {data.map((item, index) => {
            const priceState = getPriceState(item);
            const showsFromPrice = priceState === 'paid' && typeof item?.min_price === 'number' && item.min_price > 0;

            return (
              <SwiperSlide key={item?.slug || item?.id || index} onClick={() => handleSlideClick(item, index)}>
                <Content>
                  <ImageWrapper>
                    <TagsContainer>
                      {priceState && (
                        <Tag isType={priceState}>
                          <Text name="mini" color={colors.neutralLight} paddingBottom={0}>
                            {priceState === 'free' ? "Grátis" : "Pago"}
                          </Text>
                        </Tag>
                      )}

                      {item?.trending && (
                        <Tag isTrend>
                          <Column width='16px' height='16px'>
                            <Icon name="fire-filled" color={colors.occasionalPink} />
                          </Column>
                          <Text
                            name="mini"
                            color={colors.occasionalPink}
                            paddingBottom={0}
                            marginLeft={"4px"}
                          >
                            Em alta
                          </Text>
                        </Tag>
                      )}
                    </TagsContainer>

                    <CarouselFavoriteButton slug={item?.slug} initialFavorited={item?.is_favorited} eventName={item?.name} />

                    <SlideImage
                      src={item?.images?.length ? item.images[0] : '/assets/images/placeholder-events.png'}
                      alt={item?.name}
                      onError={(e) => { e.target.onerror = null; e.target.src = '/assets/images/placeholder-events.png'; }}
                    />

                    <BottomOverlay type={priceState === 'free' ? 'free' : 'paid'}>
                      <Text color={colors.neutralLight} type='bold' ellipsisAt='90%' name='small' paddingBottom={0}>
                        {item?.name}
                      </Text>
                      <Row marginTop='6px' verticalCenter>
                        <Calendar size={14} color={colors.neutralLight} />
                        <Text name='mini' color={colors.neutralLight} marginLeft='4px' paddingBottom={0}>
                          {item?.dates?.[0]?.date ? date.format(item.dates[0].date) : 'Data a definir'} · {item?.dates?.[0]?.start ? date.formatTime(item.dates[0].start) : '--:--'}
                        </Text>
                      </Row>

                      {/* O preço mínimo responde "cabe no meu bolso?" antes do clique,
                          a pergunta que decide se o card vale a visita. */}
                      {showsFromPrice && (
                        <Text name='mini' color={colors.neutralLight} marginTop='4px' paddingBottom={0}>
                          A partir de {currency.format(item.min_price)}
                        </Text>
                      )}
                    </BottomOverlay>
                  </ImageWrapper>
                </Content>
              </SwiperSlide>
            );
          })}
        </StyledSwiper>
      </CarouselContainer>

    </CarouselWrapper>
  );
};


const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const CarouselWrapper = styled.div`
  position: relative;
  margin-top: -14px;
  animation: ${fadeInUp} 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
`;

const CarouselContainer = styled.div`
  width: 100%;
  overflow: hidden;
  position: relative;
`;

/*
 * Realce do slide central: só faz sentido quando os cards giram em torno de um
 * meio. Numa faixa curta, que fica parada, o mesmo efeito deixa o primeiro card
 * maior que os outros, estoura a borda esquerda e apaga o último — o carrossel
 * parece quebrado em vez de hierarquizado.
 */
const centeredEmphasis = css`
  .swiper-slide {
    cursor: grab;
    transform: scale(0.84);
    opacity: 0.55;
    filter: brightness(0.88);
  }

  .swiper-slide-active {
    transform: scale(1.06);
    opacity: 1;
    filter: brightness(1);
    z-index: 3;
    cursor: pointer;
  }

  .swiper-slide-next,
  .swiper-slide-prev {
    transform: scale(0.93);
    opacity: 0.82;
    filter: brightness(0.94);
    z-index: 2;
  }

  @media screen and (max-width: calc(${breakpoints.lg} - 1px)) {
    .swiper-slide,
    .swiper-slide-active,
    .swiper-slide-next,
    .swiper-slide-prev {
      transform: none;
      opacity: 1;
      filter: none;
    }
  }
`;

const StyledSwiper = styled(Swiper)`
  width: 100%;
  height: auto;
  padding-top: 8px;
  padding-bottom: 4px;

  .swiper-slide {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    transition: transform 0.35s ease, opacity 0.35s ease, filter 0.35s ease;
    cursor: pointer;
  }

  ${({ $emphasis }) => $emphasis && centeredEmphasis}

  .swiper-pagination {
    position: relative;
    margin-top: 20px;
    text-align: center;
  }

  .swiper-pagination-bullet {
    background: ${colors.nightLight};
    opacity: 0.6;
    width: 8px;
    height: 8px;
    margin: 0 5px;
    border-radius: 50%;
    transition: opacity 0.3s;
    cursor: pointer;
  }

  .swiper-pagination-bullet-active {
    background: ${colors.occasionalPurple};
    opacity: 1;
  }
`;

const ArrowLeft = styled(FullyCentered)`
  position: absolute;
  left: 18px;
  top: 40%;
  transform: translateY(-50%);
  transition: all ease-in-out .2s;
  background: ${colors.neutralLight};
  border: 1px solid ${colors.night}22;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  cursor: pointer;
  z-index: 4;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

  &:hover {
    border-color: ${colors.occasionalPurple};
    svg { stroke: ${colors.occasionalPurple}; }
  }

  @media screen and (max-width: calc(${breakpoints.lg} - 1px)) {
    display: none;
  }
`;

const ArrowRight = styled(FullyCentered)`
  position: absolute;
  right: 22px;
  top: 40%;
  transform: translateY(-50%);
  transition: all ease-in-out .2s;
  background: ${colors.neutralLight};
  border: 1px solid ${colors.night}22;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  cursor: pointer;
  z-index: 4;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

  &:hover {
    border-color: ${colors.occasionalPurple};
    svg { stroke: ${colors.occasionalPurple}; }
  }

  @media screen and (max-width: calc(${breakpoints.lg} - 1px)) {
    display: none;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  overflow: hidden;
`;

const SlideImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
`;

const Content = styled(Column)`
  width: 100%;
`;

const TagsContainer = styled(Row)`
  position: absolute;
  top: 10px;
  left: 8px;
  width: auto;
  z-index: 2;
`;

const Tag = styled(FullyCentered)`
  padding: 4px 8px;
  border-radius: 6px;
  margin-right: 4px;
  flex-direction: row;
  width: auto;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  background: ${({ isType, isTrend }) =>
    isTrend ? 'rgba(255, 255, 255, 0.92)' :
    isType === 'free' ? 'rgba(43, 200, 102, 0.85)' :
    isType === 'paid' ? 'rgba(105, 12, 228, 0.85)' :
    'rgba(255, 255, 255, 0.92)'
  };
  border: none;

  ${({ isTrend }) => isTrend && `
    border: 1px solid ${colors.occasionalPink}40;
  `}
`;

const FavoriteContent = styled(FullyCentered)`
  position: absolute;
  right: 10px;
  top: 8px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  z-index: 2;
  border: none;
  padding: 0;

  &:focus-visible {
    outline: 2px solid ${colors.occasionalPurple};
    outline-offset: 2px;
  }
`;

const BottomOverlay = styled(Column)`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 10px 12px;
  background: ${({ type }) => type === 'free' ? 'rgba(43, 200, 102, 0.75)' : 'rgba(105, 12, 228, 0.75)'};
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border-radius: 0 0 12px 12px;
  z-index: 2;
`;

export default EventCarousel;
